import { useContext, useState } from "react";
import { LangContext } from "./LangContext";
import { cleanText } from "../lib/utils";

function IdeaSection({ label, items }) {
  if (!items?.length) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".10em", textTransform: "uppercase", color: "var(--pc-fg-3)", marginBottom: 3 }}>
        {label}
      </div>
      {items.map((p, j) => (
        <div key={j} style={{ display: "flex", gap: 6, fontSize: 13, color: "var(--pc-fg-2)", lineHeight: 1.55, marginBottom: 1 }}>
          <span style={{ color: "var(--pc-fg-4)" }}>·</span>
          <span style={{ flex: 1 }}>{cleanText(p)}</span>
        </div>
      ))}
    </div>
  );
}

function IdeaContent({ idea }) {
  const lang = useContext(LangContext);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 10, gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--pc-fg-1-warm)", lineHeight: 1.4, letterSpacing: "-.005em" }}>
            {cleanText(idea.title)}
          </div>
        </div>
      </div>
      {idea.missing && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".10em", textTransform: "uppercase", color: "var(--pc-fg-3)", marginBottom: 3 }}>
            {lang === "en" ? "Coverage Gap" : "보도 공백"}
          </div>
          <div style={{ fontSize: 13, color: "var(--pc-fg-2)", lineHeight: 1.55, display: "flex", gap: 6 }}>
            <span style={{ color: "var(--pc-fg-4)" }}>·</span>
            <span style={{ flex: 1 }}>{cleanText(idea.missing)}</span>
          </div>
        </div>
      )}
      <IdeaSection label={lang === "en" ? "Reasoning" : "판단 근거"} items={idea.reasoning} />
      <IdeaSection label={lang === "en" ? "Proposal" : "취재 제안"} items={idea.proposal} />
      <IdeaSection label={lang === "en" ? "Actions" : "취재 행동"} items={idea.actions} />
    </div>
  );
}

function IdeaRow({ idea, onMore }) {
  const [subIdeas, setSubIdeas] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleMore = async () => {
    if (loading || !onMore) return;
    setLoading(true);
    try {
      const result = await onMore(idea);
      if (Array.isArray(result) && result.length) {
        setSubIdeas(prev => [...prev, ...result]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pc-anim-fadeUp" style={{ borderBottom: "1px solid var(--pc-line-3)" }}>
      <div style={{ padding: "20px 0" }}>
        <IdeaContent idea={idea} />
        {onMore && (
          <div style={{ marginTop: 10, textAlign: "center" }}>
            {loading ? (
              <span style={{ fontSize: 10, color: "var(--pc-fg-3)", letterSpacing: ".10em" }}>생성 중…</span>
            ) : (
              <button onClick={handleMore} style={{
                background: "transparent", border: "none", color: "var(--pc-fg-3)",
                fontSize: 10, fontWeight: 700, letterSpacing: ".18em", textTransform: "uppercase",
                cursor: "pointer", padding: "4px 0",
              }}>More →</button>
            )}
          </div>
        )}
      </div>
      {subIdeas.length > 0 && (
        <div style={{ borderTop: "1px solid var(--pc-line-1)", background: "var(--pc-bg-2)" }}>
          {subIdeas.map((m, i) => (
            <div key={i} style={{ padding: "16px 20px", borderBottom: "1px solid var(--pc-line-1)", display: "flex", gap: 8, alignItems: "flex-start" }}>
              <span style={{ color: "var(--pc-fg-4)", fontSize: 13, marginTop: 2 }}>↳</span>
              <div style={{ flex: 1 }}><IdeaContent idea={m} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PCIdeaTab({
  ideaIn, ideas, autoIdeas, ideaL, autoIdeaL, ideaErr,
  onIdeaInputChange, onGenIdeas, onMore,
}) {
  const lang = useContext(LangContext);
  return (
    <div style={{ flex: 1, overflowY: "auto" }}>
      {/* Input */}
      <div style={{ padding: "16px 24px", borderBottom: "1px solid var(--pc-line-3)" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          background: "var(--pc-bg-3)", border: "none",
          padding: "0 14px", height: 44,
          maxWidth: 780, margin: "0 auto", borderRadius: 10,
        }}>
          <span style={{ color: "var(--pc-fg-3)", fontSize: 15, lineHeight: 1 }}>⌕</span>
          <input
            value={ideaIn || ""}
            onChange={e => onIdeaInputChange(e.target.value)}
            onKeyDown={e => e.key === "Enter" && onGenIdeas?.()}
            placeholder={lang === "en" ? "Reporting angle or hypothesis…" : "취재 각도 또는 가설…"}
            style={{
              flex: 1, background: "transparent", border: "none", outline: "none",
              color: "var(--pc-fg-1-warm)", fontSize: 14,
              fontFamily: "var(--pc-font-sans)", fontWeight: 500, minWidth: 0,
            }}
          />
          {ideaIn && (
            <button
              onClick={() => onIdeaInputChange("")}
              style={{
                width: 22, height: 22, borderRadius: 9999,
                background: "#3d4148", color: "var(--pc-fg-1)",
                fontSize: 11, cursor: "pointer", border: "none",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >✕</button>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 780, margin: "0 auto", padding: "0 24px" }}>
        {ideaL && (
          <div style={{ padding: 40, textAlign: "center", color: "var(--pc-fg-3)", fontSize: 12 }}>
            {lang === "en" ? "Cross-analyzing…" : "교차 분석 중…"}
          </div>
        )}
        {ideaErr && (
          <div style={{ padding: 16, color: "var(--pc-dir-up)", fontSize: 12 }}>{ideaErr}</div>
        )}

        {/* User hypothesis ideas */}
        {(ideas || []).map((idea, i) => <IdeaRow key={`u${i}`} idea={idea} onMore={onMore} />)}
        {ideas?.length > 0 && (
          <div style={{ borderTop: "2px solid rgba(255,255,255,.6)", margin: "8px 0" }} />
        )}

        {/* Auto ideas */}
        {autoIdeaL && (
          <div style={{ padding: 40, textAlign: "center", color: "var(--pc-fg-3)", fontSize: 12 }}>
            {lang === "en" ? "Generating story ideas…" : "취재 아이디어 생성 중…"}
          </div>
        )}
        {autoIdeas?.length > 0 && autoIdeas.map((idea, i) => <IdeaRow key={`a${i}`} idea={idea} onMore={onMore} />)}

        {!autoIdeas?.length && !autoIdeaL && (
          <div style={{ padding: "60px 24px", textAlign: "center", color: "var(--pc-fg-3)", fontSize: 12 }}>
            {lang === "en" ? "Story ideas will be generated automatically when news loads" : "뉴스가 로드되면 취재 아이디어를 자동으로 생성합니다"}
          </div>
        )}

        <div style={{ padding: "40px 0 80px", textAlign: "center" }}>
          <span style={{ fontSize: 10, color: "var(--pc-fg-4)", letterSpacing: ".10em", textTransform: "uppercase" }}>
            {lang === "en"
              ? `Auto-generated from current articles · ${(autoIdeas || []).length + (ideas || []).length}`
              : `현재 기사 기반 자동 생성 · ${(autoIdeas || []).length + (ideas || []).length}건`}
          </span>
        </div>
      </div>
    </div>
  );
}
